import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import { FormattedMessage } from 'react-intl';
import PdpSharedPurchaseButton from '../PdpSharedButtons/PdpSharedPurchaseButton';
import PdpSharedContactDealerButton from '../PdpSharedButtons/PdpSharedContactDealerButton';
import PdpSharedMakeOfferButton from '../PdpSharedButtons/PdpSharedMakeOfferButton';
import FollowSearchButton from '../../components/global/FollowSearchButton/FollowSearchButton';
import PdpSharedRequestSourcingButton from '../PdpSharedButtons/PdpSharedRequestSourcingButton';
import PdpSharedDeposit from '../PdpSharedDeposit/PdpSharedDeposit';
import PdpSharedAffirm from '../PdpSharedAffirm/PdpSharedAffirm';
import { addPageAction } from '../../utils/tracking/rumTracking';

// styles
import styles from './PdpRespSidebarButtons.scss';

// helpers
import get from 'lodash.get';
import classNames from 'classnames';

// messages
const followSearchText = searchName => (
    <p className={styles.followSearchText}>
        <FormattedMessage
            id="PdpRespSidebarButtons.followSearchText"
            defaultMessage="Follow {searchName} to receive alerts when new items arrive"
            values={{
                searchName: <span className={styles.searchName}>{searchName}</span>,
            }}
        />
    </p>
);

class PdpRespSidebarButtons extends React.Component {
    constructor(props) {
        super(props);
    }

    render() {
        const {
            relayVariables,
            user,
            item,
            displayInfo,
            quantitySelected,
            viewer,
            currency,
            isClient,
        } = this.props;
        const { itemId, userId } = relayVariables;

        if (!displayInfo || !displayInfo.ctas) {
            return null;
        }

        const {
            showPurchase,
            showMakeOffer,
            showRequestPrice,
            showRequestSourcing,
            showFollowCategory,
        } = displayInfo.ctas;
        const isVerifiedTrade = get(user, 'isVerifiedTrade') || false;
        const buttonsClass = classNames(styles.buttons, {
            [styles.hasQuantitySelect]: !!quantitySelected,
        });

        addPageAction('respPdpCTAsRendered', { itemId });

        return (
            <div className={buttonsClass} data-tn="item-actions">
                {/* Purchase */}
                {!!showPurchase && (
                    <PdpSharedPurchaseButton
                        itemId={itemId}
                        item={item}
                        userId={userId}
                        quantitySelected={quantitySelected}
                    />
                )}
                {/* Request Price */}
                {!!showRequestPrice && (
                    <PdpSharedContactDealerButton type={'REQUEST_PRICE'} item={item} user={user} />
                )}
                {/* Make Offer */}
                {!!showMakeOffer && (
                    <PdpSharedMakeOfferButton
                        itemId={itemId}
                        item={item}
                        userId={userId}
                        user={user}
                        quantitySelected={quantitySelected}
                    />
                )}

                <PdpSharedDeposit userId={userId} item={item} />

                {/* Request Sourcing Help */}
                {!!showRequestSourcing && (
                    <div>
                        <div className={styles.requestSourcing}>
                            <FormattedMessage
                                id="PdpRespSidebarButtons.requestSourcingSimilarText"
                                defaultMessage="Looking for something similar?"
                            />
                            <p className={styles.requestSourcingHelp}>
                                <FormattedMessage
                                    id="PdpRespSidebarButtons.requestSourcingHelpText"
                                    defaultMessage="Let our sourcing team help you for free."
                                />
                            </p>
                        </div>
                        <PdpSharedRequestSourcingButton item={item} user={user} />
                    </div>
                )}
                {/* Follow Search */}
                {showFollowCategory && isVerifiedTrade ? (
                    // trade users
                    <div className={styles.followTrade}>
                        {followSearchText(get(item, 'saveSearchData.name'))}
                        <div>
                            <FollowSearchButton
                                size="small"
                                className={styles.followButtonTrade}
                                userId={userId}
                                favoritesConnection={get(this.props, 'user.favorites')}
                                page={get(this.props, 'item.saveSearchData.page')}
                            />
                        </div>
                    </div>
                ) : (
                    !!showFollowCategory && (
                        // non trade users
                        <div>
                            {followSearchText(get(item, 'saveSearchData.name'))}
                            <FollowSearchButton
                                userId={userId}
                                user={user}
                                favoritesConnection={get(this.props, 'user.favorites') || null}
                                page={get(this.props, 'item.saveSearchData.page')}
                                fullWidth
                            />
                        </div>
                    )
                )}
                {/* Affirm */}
                {isClient && (
                    <PdpSharedAffirm currency={currency} viewer={viewer} user={user} item={item} />
                )}
            </div>
        );
    }
}

PdpRespSidebarButtons.propTypes = {
    relayVariables: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    isClient: PropTypes.bool,
    relay: PropTypes.object,
    userId: PropTypes.string,
    displayInfo: PropTypes.object,
    updateSavedSearchesVariables: PropTypes.func,
    quantitySelected: PropTypes.string,
    currency: PropTypes.string,
};

export default createFragmentContainer(PdpRespSidebarButtons, {
    viewer: graphql`
        fragment PdpRespSidebarButtons_viewer on Viewer {
            ...PdpSharedAffirm_viewer
        }
    `,
    user: graphql`
        fragment PdpRespSidebarButtons_user on User {
            isVerifiedTrade
            ...PdpSharedContactDealerButton_user
            ...PdpSharedRequestSourcingButton_user
            ...PdpSharedMakeOfferButton_user
            ...PdpSharedAffirm_user
            favorites(first: 1, types: [SEARCH], pages: $favoritePages)
                @include(if: $fetchFavorites) {
                ...FollowSearchButton_favoritesConnection
            }
        }
    `,
    item: graphql`
        fragment PdpRespSidebarButtons_item on Item {
            isInProcurement(userId: $userId) @include(if: $fetchUser)
            saveSearchData {
                name
                page
            }
            ...PdpSharedContactDealerButton_item
            ...PdpSharedRequestSourcingButton_item
            ...PdpSharedRequestPurchasingServiceButton_item
            ...PdpSharedPurchaseButton_item
            ...PdpSharedMakeOfferButton_item
            ...PdpSharedDeposit_item
            ...PdpSharedAffirm_item
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarButtons_displayInfo on ItemDisplayInfo {
            ctas {
                showPurchase
                showMakeOffer
                showRequestPrice
                showRequestSourcing
                showFollowCategory
            }
        }
    `,
});
